import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { IntlContext } from "context";
import { MoreVertical } from "lucide-react";
import { useDeleteService, useUpdateService } from "hooks/service";
import { ServiceInterface } from "types/service";
import { SUBCATEGORY_TO_CATEGORY, SubCategory } from "types/subCategory";
import { buildRoute } from "routes";
import { Button } from "components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { isServiceEffectivelyActive } from "../../profileCompletion";
import { getCategoryStyle } from "../categoryStyle";
import { toast } from "sonner";

interface Props {
    service: ServiceInterface;
    minSalary: number;
    profileComplete: boolean;
    onUpdated: () => void;
}

const ServiceCard: React.FC<Props> = ({ service, minSalary, profileComplete, onUpdated }) => {
    const intl = useContext(IntlContext);
    const updateServiceMutation = useUpdateService();
    const deleteServiceMutation = useDeleteService();
    const [enableError, setEnableError] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const categoryKey = service.category.toString();
    const { Icon, boxClass, iconClass } = getCategoryStyle(categoryKey);
    const subCategory = service.subcategory as SubCategory | undefined;

    const title = subCategory
        ? intl.formatMessage(
              `subcategory.${SUBCATEGORY_TO_CATEGORY[subCategory].toLocaleLowerCase()}.${subCategory.toLocaleLowerCase()}.label`
          )
        : intl.formatMessage(`category.${categoryKey.toLocaleLowerCase()}`);

    const categoryLabel = intl.formatMessage(`category.${categoryKey.toLocaleLowerCase()}`);

    const isRecord = (value: unknown): value is Record<string, unknown> =>
        typeof value === "object" && value !== null;

    const getEnablePriceErrorMessage = (err: unknown): string | undefined => {
        if (!isRecord(err) || !("response" in err) || !isRecord(err.response)) return undefined;
        const response = err.response;
        if (!("data" in response) || !isRecord(response.data)) return undefined;
        const data = response.data;
        if (!("data" in data) || !isRecord(data.data)) return undefined;
        const inner = data.data;
        if (!("price" in inner) || !isRecord(inner.price)) return undefined;
        const message = inner.price.message;
        return typeof message === "string" ? message : undefined;
    };

    const handleToggleActive = async () => {
        try {
            setEnableError("");
            await updateServiceMutation.mutateAsync({
                id: service.id,
                data: { ...service, is_active: !service.is_active },
            });
            onUpdated();
        } catch (err: unknown) {
            const message = getEnablePriceErrorMessage(err);
            if (message) {
                setEnableError(message);
            }
        }
    };

    const priceTooLow = Number(service.price) < minSalary;
    const isDescriptionMissing = !service.description?.trim();
    const effectivelyActive = isServiceEffectivelyActive(service.is_active, profileComplete);
    const canEnable = !effectivelyActive && !priceTooLow && profileComplete && !isDescriptionMissing;

    const handleDeleteService = async () => {
        try {
            await deleteServiceMutation.mutateAsync(service.id);
            setShowDeleteModal(false);
            toast.success(intl.formatMessage("service.message.delete_success"));
            onUpdated();
        } catch {
            // Keep the dialog open so the user can retry or cancel.
        }
    };

    return (
        <article className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-start gap-4">
                    <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${boxClass}`}
                    >
                        <Icon className={`h-5 w-5 ${iconClass}`} strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-bold text-gray-900">{title}</h3>
                        <p className="mt-1 text-sm text-slate-500">{categoryLabel}</p>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-4 sm:gap-6">
                    <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">
                            {intl.formatMessage("our_services.list.label.price", {
                                price: Number(service.price),
                            })}
                        </div>
                        <div className="text-xs text-slate-500">Tarif horaire</div>
                    </div>

                    {effectivelyActive ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Actif
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                            Inactif
                        </span>
                    )}

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 rounded-lg">
                                <MoreVertical className="h-4 w-4" />
                                <span className="sr-only">Actions</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl">
                            <DropdownMenuItem asChild>
                                <Link to={buildRoute.myServicesEdit(service.id)}>Modifier</Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => void handleToggleActive()}
                                disabled={!effectivelyActive && !canEnable}
                            >
                                {effectivelyActive
                                    ? intl.formatMessage("service.label.disable")
                                    : intl.formatMessage("service.label.enable")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                                onSelect={() => setShowDeleteModal(true)}
                            >
                                {intl.formatMessage("service.label.delete")}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{intl.formatMessage("service.label.modal.title")}</DialogTitle>
                        <DialogDescription>
                            {intl.formatMessage("service.label.modal.message")}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                            {intl.formatMessage("generic.action.cancel")}
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleteServiceMutation.isPending}
                            onClick={() => void handleDeleteService()}
                        >
                            {intl.formatMessage("generic.action.confirm")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {enableError && <p className="mt-2 text-sm text-destructive">{enableError}</p>}
            {isDescriptionMissing && (
                <p className="mt-2 text-sm text-amber-700">
                    {intl.formatMessage("service.label.missing_description")}{" "}
                    <Link
                        to={buildRoute.myServicesEdit(service.id)}
                        className="font-semibold text-primary hover:underline"
                    >
                        {intl.formatMessage("service.label.edit")}
                    </Link>
                </p>
            )}
            {priceTooLow && !effectivelyActive && (
                <p className="mt-2 text-sm text-amber-700">
                    Le tarif horaire doit être supérieur à {minSalary}€ pour activer ce service.
                </p>
            )}
        </article>
    );
};

export default ServiceCard;
